import ExcelJS from 'exceljs';
import type { Convention, ConventionEmployee, Proposition } from '../types/convention';

export interface ReclassementRow {
  employee: ConventionEmployee;
  convention: Convention;
}

function getPropositions(employee: ConventionEmployee): Proposition[] {
  if (employee.propositions && employee.propositions.length > 0) return employee.propositions;
  if (employee.entreprise || employee.poste || employee.contrat || employee.cv_envoye || employee.entretien || employee.resultat || employee.ove) {
    return [{
      offerId: '',
      entreprise: employee.entreprise || '',
      poste: employee.poste || '',
      contrat: employee.contrat || '',
      cv_envoye: employee.cv_envoye || '',
      entretien: employee.entretien || '',
      resultat: employee.resultat || '',
      ove: employee.ove || '',
      notes: employee.notes || '',
    }];
  }
  return [];
}

function formatPropositionCell(props: Proposition[]): string {
  if (props.length === 0) return '';
  return props.map(p => {
    const lines: string[] = [];
    if (p.cv_envoye) lines.push(`Date: ${p.cv_envoye}`);
    if (p.entreprise) lines.push(`Entreprise: ${p.entreprise}`);
    if (p.poste) lines.push(`Poste: ${p.poste}`);
    if (p.contrat) lines.push(`Contrat: ${p.contrat}`);
    if (p.resultat) lines.push(`Résultat: ${p.resultat}`);
    return lines.join('\n');
  }).join('\n---\n');
}

export async function generateReclassementDocument(rows: ReclassementRow[]): Promise<void> {
  const response = await fetch('/data/Modele_vierge_export_reclassment.xlsx');
  if (!response.ok) {
    throw new Error('Template Excel introuvable');
  }
  const buffer = await response.arrayBuffer();

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);

  const ws = wb.getWorksheet(1);
  if (!ws) throw new Error('Feuille Excel introuvable dans le template');

  const startRow = 2;

  rows.forEach((row, index) => {
    const e = row.employee;
    const props = getPropositions(e);
    const rowNum = startRow + index;

    const fullName = `${e.prenom} ${e.nom}`.trim();
    const propositionCell = formatPropositionCell(props);
    const oveValue = props.length > 0 ? props.map(p => p.ove).filter(Boolean).join(', ') : '';
    const commissionComments = [
      e.notes,
      ...props.map(p => p.notes),
    ].filter(Boolean).filter((comment, commentIndex, comments) => comments.indexOf(comment) === commentIndex).join('\n');

    const values: (string | number)[] = [
      index + 1,
      fullName,
      e.ville_dept || '',
      e.age ?? '',
      e.poste_actuel || '',
      '',
      commissionComments,
      e.statut || '',
      propositionCell,
      oveValue,
      e.consultant_attribue || '',
    ];

    const excelRow = ws.getRow(rowNum);
    values.forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value;
      cell.alignment = { vertical: 'top', wrapText: true };
    });
    excelRow.commit();
  });

  const today = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
  const outBuffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([outBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `suivi-reclassement-${today}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
